import { useState } from 'react';
import ServiceNavbar from '../components/ServiceNavbar';
import Footer from '../../home/components/Footer';
import { Link } from 'react-router-dom';
import EnquiryForm from './components/EnquiryForm';
import Testimonials from './components/Testimonials';

export default function WebDesign() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const faqs = [
    {
      question: 'How long does it take to build a website?',
      answer: 'The timeline depends on the project scope. A Starter package typically takes 2-3 weeks, Professional packages 4-6 weeks, and Enterprise projects 8-12 weeks. We\'ll provide a detailed timeline during our initial consultation based on your specific requirements.'
    },
    {
      question: 'What do I need to provide to get started?',
      answer: 'We\'ll need your brand assets (logo, colors, fonts), content (text, images, videos), and a clear understanding of your goals. Don\'t worry if you don\'t have everything ready—we can help with content strategy and source professional imagery for you.'
    },
    {
      question: 'Will my website be mobile-friendly?',
      answer: 'Absolutely! All our websites are built with a mobile-first approach, ensuring they look and function perfectly on smartphones, tablets, and desktops. With over 60% of web traffic coming from mobile devices, responsive design is non-negotiable.'
    },
    {
      question: 'Can I update the website myself after it\'s built?',
      answer: 'Yes! We build websites with user-friendly content management systems (CMS) that allow you to easily update text, images, and add new pages without any coding knowledge. We also provide training to ensure you\'re comfortable managing your site.'
    },
    {
      question: 'Do you provide hosting and domain services?',
      answer: 'We can help you set up reliable hosting and register your domain name. We recommend premium hosting solutions that ensure fast load times, security, and 99.9% uptime. Hosting costs are separate from design packages and typically range from $20-100/month depending on your needs.'
    },
    {
      question: 'What if I need changes after the website launches?',
      answer: 'All packages include post-launch support (30-365 days depending on your plan). During this period, we\'ll fix any bugs and make minor adjustments. For ongoing updates and maintenance, we offer monthly retainer plans starting at $299/month.'
    },
    {
      question: 'Will my website be optimized for search engines?',
      answer: 'Yes, SEO is built into every website we create. This includes proper heading structure, meta tags, fast loading speeds, mobile optimization, and clean code. For comprehensive SEO strategies including content marketing and link building, we offer dedicated SEO services.'
    },
    {
      question: 'What\'s the difference between a template and custom design?',
      answer: 'Templates are pre-made designs that we customize with your branding, while custom designs are created from scratch specifically for your business. Custom designs offer unique layouts, better brand alignment, and more flexibility, which is why all our packages include custom design work.'
    }
  ];

  const features = [
    {
      icon: 'ri-smartphone-line',
      title: 'Responsive Design',
      description: 'Websites that look stunning and function flawlessly on every device, from desktop to mobile.'
    },
    {
      icon: 'ri-layout-masonry-line',
      title: 'UX/UI Optimization',
      description: 'User-centered design that creates intuitive experiences and guides visitors to take action.'
    },
    {
      icon: 'ri-search-eye-line',
      title: 'SEO Foundation',
      description: 'Built-in search engine optimization to help your site rank higher and attract organic traffic.'
    },
    {
      icon: 'ri-speed-line',
      title: 'Performance Tuning',
      description: 'Lightning-fast load times and optimized performance for better user experience and conversions.'
    },
    {
      icon: 'ri-code-s-slash-line',
      title: 'Custom Development',
      description: 'Tailored functionality and features built specifically for your unique business requirements.'
    },
    {
      icon: 'ri-shopping-cart-2-line',
      title: 'E-commerce Solutions',
      description: 'Powerful online stores with seamless checkout, inventory management, and payment integration.'
    }
  ];

  const technologies = [
    { icon: 'ri-reactjs-line', name: 'React', desc: 'Modern UI' },
    { icon: 'ri-wordpress-line', name: 'WordPress', desc: 'CMS Platform' },
    { icon: 'ri-shopping-bag-3-line', name: 'Shopify', desc: 'E-commerce' },
    { icon: 'ri-layout-4-line', name: 'Webflow', desc: 'No-code' },
    { icon: 'ri-database-2-line', name: 'Headless CMS', desc: 'Flexible Content' },
    { icon: 'ri-cloud-line', name: 'Cloud Hosting', desc: 'Scalable' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <ServiceNavbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center pt-28">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/871eedd9d689033d321d62850aee3469.jpg"
            alt="Web Design Services"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-transparent"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 w-full">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00989e]/20 rounded-full mb-6">
              <i className="ri-layout-masonry-line text-[#00989e]"></i>
              <span className="text-[#00b4a6] text-sm font-medium">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Web Design
            </h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Beautiful, conversion-focused websites that captivate visitors and drive results. From stunning visuals to seamless functionality, we build digital experiences that work.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="#enquiry" className="px-8 py-4 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap">
                Start Your Project
              </a>
              <Link to="/case-studies" className="px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-slate-900 transition-colors cursor-pointer whitespace-nowrap">
                View Our Work
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              What's Included
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need for a website that performs and converts
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-shadow">
                <div className="w-14 h-14 bg-[#00989e]/10 rounded-xl flex items-center justify-center mb-6">
                  <i className={`${feature.icon} text-2xl text-[#00989e]`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Technologies & Platforms
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We work with the best tools to deliver exceptional results
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((item, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 flex flex-col items-center justify-center aspect-square hover:scale-105 transition-transform">
                  <i className={`${item.icon} text-4xl text-[#00989e] mb-3`}></i>
                  <span className="text-white font-medium text-sm text-center">{item.name}</span>
                  <span className="text-gray-400 text-xs text-center mt-1">{item.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Web Design Process
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              A proven approach to creating websites that deliver results
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'Understand your goals, audience, and requirements' },
              { step: '02', title: 'Wireframe', desc: 'Plan structure, user flows, and content hierarchy' },
              { step: '03', title: 'Design', desc: 'Create stunning visuals and interactive prototypes' },
              { step: '04', title: 'Develop', desc: 'Build, test, and launch your perfect website' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="text-6xl font-bold text-[#00989e]/30 mb-4">{item.step}</div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Web Design Matters Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Why Great Web Design Matters
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Your website is often the first impression customers have of your business. A well-designed site builds credibility, engages visitors, and converts them into loyal customers.
              </p>
              <div className="space-y-4">
                {[
                  { stat: '94%', text: 'of first impressions are design-related' },
                  { stat: '88%', text: 'won\'t return after a bad experience' },
                  { stat: '200%', text: 'conversion increase with good UX' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="text-3xl font-bold text-[#00989e]">{item.stat}</div>
                    <div className="text-gray-600">{item.text}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://readdy.ai/api/search-image?query=Modern%20website%20design%20mockups%20displayed%20on%20multiple%20devices%20including%20laptop%20tablet%20and%20smartphone%20showing%20responsive%20design%2C%20teal%20accent%20colors%20in%20the%20interface%2C%20professional%20photography%20style%20on%20clean%20white%20background%2C%20minimalist%20modern%20aesthetic%20with%20soft%20shadows&width=600&height=500&seq=webdesign-why-1&orientation=landscape"
                alt="Responsive Web Design"
                className="w-full h-[500px] object-cover rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              What You Get
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A complete website package ready to launch and grow your business
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-pages-line', title: 'Custom Pages', desc: 'Unique designs for every page' },
              { icon: 'ri-smartphone-line', title: 'Mobile Responsive', desc: 'Perfect on all devices' },
              { icon: 'ri-seo-line', title: 'SEO Optimized', desc: 'Built to rank higher' },
              { icon: 'ri-speed-up-line', title: 'Fast Loading', desc: 'Optimized performance' },
              { icon: 'ri-shield-check-line', title: 'SSL Security', desc: 'Secure & protected' },
              { icon: 'ri-bar-chart-box-line', title: 'Analytics Setup', desc: 'Track your success' },
              { icon: 'ri-customer-service-2-line', title: 'Training & Support', desc: 'Learn to manage it' },
              { icon: 'ri-refresh-line', title: 'Maintenance Plan', desc: 'Keep it running smooth' }
            ].map((item, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6 hover:bg-[#00989e]/5 transition-colors">
                <div className="w-12 h-12 bg-[#00989e]/10 rounded-lg flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-xl text-[#00989e]`}></i>
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-gray-600 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Web Design Packages
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the perfect package for your business needs and budget
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Starter Package */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow border border-gray-100">
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-rocket-line text-2xl text-slate-700"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Starter</h3>
                <p className="text-gray-500 text-sm">Perfect for small businesses</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$2,500</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Up to 5 custom pages',
                  'Mobile responsive design',
                  'Basic SEO setup',
                  'Contact form integration',
                  'Social media links',
                  '1 round of revisions',
                  '30 days support'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#00989e] mt-0.5"></i>
                    <span className="text-gray-600 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <a href="#enquiry" className="block w-full py-3 border-2 border-slate-900 text-slate-900 font-semibold rounded-full hover:bg-slate-900 hover:text-white transition-colors cursor-pointer whitespace-nowrap text-center">
                Get Started
              </a>
            </div>

            {/* Professional Package */}
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 shadow-xl relative transform md:scale-105">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1 bg-[#00989e] text-white text-sm font-medium rounded-full whitespace-nowrap">Most Popular</span>
              </div>
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-[#00989e]/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-award-line text-2xl text-[#00989e]"></i>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Professional</h3>
                <p className="text-gray-400 text-sm">For growing businesses</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-white">$5,500</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Up to 12 custom pages',
                  'Advanced responsive design',
                  'Full SEO optimization',
                  'CMS integration',
                  'Blog setup',
                  'Email marketing integration',
                  'Analytics dashboard',
                  '3 rounds of revisions',
                  '90 days support'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#00989e] mt-0.5"></i>
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <a href="#enquiry" className="block w-full py-3 bg-[#00989e] text-white font-semibold rounded-full hover:bg-[#007a7f] transition-colors cursor-pointer whitespace-nowrap text-center">
                Get Started
              </a>
            </div>

            {/* Enterprise Package */}
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-shadow border border-gray-100">
              <div className="text-center mb-8">
                <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-building-2-line text-2xl text-slate-700"></i>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Enterprise</h3>
                <p className="text-gray-500 text-sm">For large organizations</p>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">$12,000</span>
                </div>
              </div>
              <ul className="space-y-4 mb-8">
                {[
                  'Unlimited custom pages',
                  'E-commerce functionality',
                  'Custom web application',
                  'Advanced integrations',
                  'Multi-language support',
                  'Priority development',
                  'Dedicated project manager',
                  'Unlimited revisions',
                  '1 year support & maintenance'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#00989e] mt-0.5"></i>
                    <span className="text-gray-600 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              <a href="#enquiry" className="block w-full py-3 border-2 border-slate-900 text-slate-900 font-semibold rounded-full hover:bg-slate-900 hover:text-white transition-colors cursor-pointer whitespace-nowrap text-center">
                Contact Us
              </a>
            </div>
          </div>
          <p className="text-center text-gray-500 text-sm mt-8">
            Need a custom solution? <a href="/contact" className="text-[#00989e] font-medium hover:underline cursor-pointer">Contact us</a> for a personalized quote.
          </p>
        </div>
      </section>

      {/* Stepped Enquiry Form — right after pricing */}
      <EnquiryForm />

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Got questions? We've got answers. Here's everything you need to know about our web design services.
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div 
                key={index} 
                className="border border-gray-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left bg-white hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  <span className="text-lg font-semibold text-slate-900 pr-4">{faq.question}</span>
                  <div className={`w-8 h-8 flex items-center justify-center rounded-full bg-[#00989e]/10 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line text-[#00989e] text-xl"></i>
                  </div>
                </button>
                <div 
                  className={`overflow-hidden transition-all duration-300 ${openFaq === index ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="p-6 pt-0 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">Still have questions?</p>
            <a href="/contact" className="inline-flex items-center gap-2 text-[#00989e] font-semibold hover:underline cursor-pointer">
              <span>Contact our team</span>
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#00989e] to-[#007a7f]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Ready to Build Your Website?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let's create a stunning website that converts visitors into customers.
          </p>
          <a href="#enquiry" className="inline-block px-10 py-4 bg-white text-[#00989e] font-bold rounded-full hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
            Get a Free Quote
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
